import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { createOneTimeToken } from "helpers/auth-service";
import useUser from "../../hooks/useuser";
import { sessionStorageKeys } from "../types";
import AssessmentLoading from "../components/assessment-loading";
import { useAssessments } from "../context/assessment-provider";
import { logoutUser } from "../../helpers/user-session";
import useMyPathReferrer from "../hooks/useMyPathReferrer";

async function getReturnUrl() {
  let returnUrl = sessionStorage.getItem(sessionStorageKeys.returnUrl);
  if (sessionStorage.getItem(sessionStorageKeys.oneTimeToken) === "true") {
    const oneTimeToken = await createOneTimeToken();
    returnUrl = returnUrl?.includes("?") ? `${returnUrl}&ott=${oneTimeToken}` : `${returnUrl}?ott=${oneTimeToken}`;
  }
  return returnUrl;
}

/**
 * Component that handles redirection based on:
 * - the referrer stored in session storage
 * - whether the student is currently in a multi-diagnostic flow
 * - whether the student completed the diagnostic
 *
 * @returns {JSX.Element} A loading component while the redirection is being processed.
 */
function DiagnosticReturn() {
  const { assessments, loading } = useAssessments();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { user } = useUser();
  const { returnToMyPathLandingPage } = useMyPathReferrer();

  useEffect(() => {
    if (!loading && assessments && user) {
      const diagnosticSubject = sessionStorage.getItem(sessionStorageKeys.diagnosticSubject);
      const diagnosticTitle = sessionStorage.getItem(sessionStorageKeys.diagnosticTitle);
      const referrer = sessionStorage.getItem(sessionStorageKeys.referrer);
      sessionStorage.removeItem(sessionStorageKeys.diagnosticTitle);

      if (search?.toLowerCase().includes("incomplete=true")) {
        sessionStorage.removeItem(sessionStorageKeys.diagnosticSubject);
        if (referrer === "L&L") {
          logoutUser(user);
          return;
        }
        if (referrer === "MyPath") {
          returnToMyPathLandingPage();
          return;
        }
      }

      if (sessionStorage.getItem(sessionStorageKeys.multiDiagnostic) && diagnosticSubject && diagnosticTitle) {
        const remainingDiagnostics = assessments.filter(
          (assessment) =>
            assessment.title !== diagnosticTitle &&
            assessment.subject.toLowerCase() === diagnosticSubject.toLowerCase() &&
            assessment.assessmentType === "diagnostic"
        );
        if (remainingDiagnostics.length < 2) {
          sessionStorage.removeItem(sessionStorageKeys.multiDiagnostic);
        }

        if (remainingDiagnostics.length === 1) {
          navigate("/assessments", {
            replace: true,
            state: { launchDiagnostic: true, testNumber: remainingDiagnostics[0].testNumber }
          });
          return;
        }

        if (remainingDiagnostics.length > 0) {
          navigate("/assessments", { replace: true });
          return;
        }
      }

      // zero remaining multi-diagnostics, or single diagnostic
      sessionStorage.removeItem(sessionStorageKeys.multiDiagnostic);
      sessionStorage.removeItem(sessionStorageKeys.diagnosticSubject);

      if (referrer === "MyPath") {
        returnToMyPathLandingPage();
      } else if (referrer === "L&L") {
        getReturnUrl()
          .then((returnUrl) => {
            if (returnUrl) {
              window.location.replace(returnUrl);
            }
          })
          .catch((error) => {
            console.error("Error retrieving return URL", error);
            navigate("/assessments", { replace: true });
          });
      } else {
        navigate("/assessments", { replace: true });
      }
    }
  }, [assessments, loading, navigate]);

  return <AssessmentLoading />;
}

export default DiagnosticReturn;
